import { useEffect, useMemo, useState } from 'react'
import { api, type Auswertung, type StationBasis, type StationenBild } from '../api'
import { anzahl, euro, euroKurz } from '../format'
import { Auswertungsraster } from '../komponenten/Auswertungsraster'
import { Karte } from '../komponenten/Karte'
import { MehrfachLinie } from '../komponenten/MehrfachLinie'
import { Tipp } from '../komponenten/Hinweis'

function Stationsname({ zeile, mehrere }: { zeile: StationBasis; mehrere: boolean }) {
  const text = `${mehrere ? `${zeile.standort} · ` : ''}${zeile.station}`
  return zeile.benannt || zeile.terminal_ref === null ? <>{text}</> : (
    <Tipp text="Für diese Kennung ließ sich kein eindeutiger Gerätename ableiten. Sie können die Station unter Einrichtung benennen." fokus>
      <span>{text}</span>
    </Tipp>
  )
}

export function Stationen({ von, bis, betriebe }: {
  von: string; bis: string; betriebe: string[]
}) {
  const [d, setD] = useState<StationenBild | null>(null)
  const [kacheln, setKacheln] = useState<Auswertung[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [sortierung, setSortierung] = useState<{
    feld: 'umsatz' | 'positionen' | 'positionswert'; richtung: 'auf' | 'ab'
  }>({ feld: 'umsatz', richtung: 'ab' })

  useEffect(() => {
    let weg = false
    api.auswertungen('stationen').then(async (liste) => {
      const aktiv = liste.filter((k) => k.aktiv).map((k) => k.schluessel)
      const bild = await api.stationen(von, bis, betriebe, aktiv)
      if (!weg) { setKacheln(liste); setD(bild) }
    }).catch((e) => { if (!weg) setFehler(e.message) })
    return () => { weg = true }
  }, [von, bis, betriebe.join(',')])

  const mehrere = new Set(d?.stationen?.map((z) => z.location_id) ?? []).size > 1
  const stationen = useMemo(() => [...(d?.stationen ?? [])].sort((a, b) => {
    const unterschied = Number(a[sortierung.feld]) - Number(b[sortierung.feld])
    return (sortierung.richtung === 'auf' ? unterschied : -unterschied)
      || a.station.localeCompare(b.station, 'de')
  }), [d, sortierung])
  const sortieren = (feld: 'umsatz' | 'positionen' | 'positionswert') => {
    setSortierung((alt) => ({
      feld,
      richtung: alt.feld === feld && alt.richtung === 'ab' ? 'auf' : 'ab',
    }))
  }
  const sortierkopf = (feld: 'umsatz' | 'positionen' | 'positionswert', text: string) => (
    <button className="stationssortierung" onClick={() => sortieren(feld)}
      aria-label={`${text} sortieren`}>
      {text}<span aria-hidden="true">{sortierung.feld === feld ? (sortierung.richtung === 'ab' ? ' ↓' : ' ↑') : ''}</span>
    </button>
  )
  const kennzahlen = (
    <table>
      <thead><tr><th>Station</th><th className="r">{sortierkopf('umsatz', 'Umsatz')}</th>
        <th className="r">{sortierkopf('positionen', 'Positionen')}</th>
        <th className="r">{sortierkopf('positionswert', 'Ø Position')}</th></tr></thead>
      <tbody>{stationen.map((z) => (
        <tr key={`${z.location_id}:${z.terminal_ref ?? 'ohne'}`}>
          <td><Stationsname zeile={z} mehrere={mehrere} /></td>
          <td className="r zahl">{euro(z.umsatz)}</td>
          <td className="r zahl">{anzahl(z.positionen)}</td>
          <td className="r zahl">{euro(z.positionswert)}</td>
        </tr>
      ))}</tbody>
    </table>
  )

  const warengruppen = useMemo(() => {
    const gruppiert = new Map<string, { station: StationBasis; gruppen: NonNullable<StationenBild['warengruppen']> }>()
    for (const zeile of d?.warengruppen ?? []) {
      const schluessel = `${zeile.location_id}:${zeile.terminal_ref ?? 'ohne'}`
      const eintrag = gruppiert.get(schluessel) ?? { station: zeile, gruppen: [] }
      eintrag.gruppen.push(zeile)
      gruppiert.set(schluessel, eintrag)
    }
    return [...gruppiert.values()]
  }, [d])

  const verlauf = useMemo(() => {
    const zeilen = d?.verlauf ?? []
    const stunden = [...new Set(zeilen.map((z) => z.stunde))].sort((a, b) => a - b)
    const stationen = new Map<string, StationBasis>()
    zeilen.forEach((z) => stationen.set(`${z.location_id}:${z.terminal_ref ?? 'ohne'}`, z))
    return {
      etiketten: stunden.map((h) => `${String(h).padStart(2, '0')} Uhr`),
      reihen: [...stationen.entries()].map(([schluessel, z]) => ({
        schluessel,
        name: `${mehrere ? `${z.standort} · ` : ''}${z.station}`,
        werte: stunden.map((h) => Number(zeilen.find((x) =>
          `${x.location_id}:${x.terminal_ref ?? 'ohne'}` === schluessel && x.stunde === h)?.umsatz ?? 0)),
      })),
    }
  }, [d, mehrere])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (!d) return <div className="laedt">Wird geladen …</div>

  const inhalt = {
    'stationen.kennzahlen': () => (
      <Karte titel="Stationen im Vergleich" neben="Umsatz, Buchungszeilen und durchschnittlicher Positionswert">
        {stationen.length ? kennzahlen : <div className="leer">Keine Stationen im Zeitraum.</div>}
      </Karte>
    ),
    'stationen.warengruppen': () => (
      <Karte titel="Warengruppenmix je Station">
        {warengruppen.length ? <div className="stationsmix">{warengruppen.map(({ station, gruppen }) => (
          <div className="stationsmix-zeile" key={`${station.location_id}:${station.terminal_ref ?? 'ohne'}`}>
            <div className="stationsmix-name"><Stationsname zeile={station} mehrere={mehrere} /></div>
            <div className="stationsmix-balken" aria-label={`Warengruppenanteile ${station.station}`}>
              {gruppen.map((g, i) => <Tipp key={`${g.warengruppe}:${i}`}
                text={`${g.warengruppe}: ${anzahl(g.positionen)} Positionen · ${euro(g.umsatz)}`}
                className={`stationsmix-teil farbe-${(i % 6) + 1}`}
                style={{ width: `${Math.max(0, Number(g.anteil) * 100)}%` }} fokus>
                <span />
              </Tipp>)}
            </div>
            <div className="stationsmix-legende">
              {gruppen.slice(0, 4).map((g, i) => <span key={`${g.warengruppe}:${i}`}>
                <i className={`farbe-${(i % 6) + 1}`} />{g.warengruppe} {Math.round(Number(g.anteil) * 100)} %
              </span>)}
              {gruppen.length > 4 && <span className="dezent">+ {gruppen.length - 4} weitere</span>}
            </div>
          </div>
        ))}</div> : <div className="leer">Keine Warengruppen im Zeitraum.</div>}
      </Karte>
    ),
    'stationen.verlauf': () => (
      <Karte titel="Stationen im Tagesverlauf" neben="Umsatz nach Abschlussstunde">
        {verlauf.reihen.length ? <MehrfachLinie etiketten={verlauf.etiketten} reihen={verlauf.reihen}
          formatiere={euro} formatiereAchse={euroKurz} abNull />
          : <div className="leer">Keine Positionen im Zeitraum.</div>}
      </Karte>
    ),
  }
  return <Auswertungsraster kacheln={kacheln} inhalt={inhalt} />
}
